import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';

import { AccountProfileElement, ConfirmActionModalElement } from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { Site } from '~/interfaces/site';
import type { loader, RootLoaderData } from '~/root';
import { getContrastingColor, hasMinimumContrast } from '~/utils/getContrastingColor';

import UnsubscribeAction from '../../_actions/UnsubscribeAction';

interface Props {
  site: Site;
}

const SuspendedPage = ({ site }: Props) => {
  const { t } = useTranslation();
  const { handleOpenModal, handleCloseModal, currentModal } = useGlobalModals({ useLocalState: true });
  const { subscription } = useRouteLoaderData<typeof loader>('root') as unknown as RootLoaderData;

  const { email, first_name: firstName, last_name: lastName, avatar_url: avatarUrl } = subscription || {};
  const isInactive = subscription?.status === 'inactive';

  const primaryThemeBackgroundColor = site.primary_theme.primary_color;
  const primaryThemeTextOnBackgroundColor = hasMinimumContrast(
    site.primary_theme.background_color,
    site.primary_theme.primary_color,
  )
    ? site.primary_theme.background_color
    : getContrastingColor(site.primary_theme.primary_color);

  return (
    <div
      className="bg-gray-100"
      style={
        {
          '--pt-background-color': primaryThemeBackgroundColor,
          '--pt-text-on-background-color': primaryThemeTextOnBackgroundColor,
        } as CSSProperties
      }
    >
      <UnsubscribeAction publication={site?.publication} showSubmitButton={false} onSuccess={handleCloseModal}>
        {({ isLoading, submitForm }) => (
          <ConfirmActionModalElement
            element={{ type: 'confirmActionModal' }}
            title={t('manage.downgrade.title')}
            cancelLabel={t('shared.cta.cancel')}
            submitLabel={t('shared.cta.unsubscribe')}
            message={t('errors.website_suspended.description')}
            isSubmitting={isLoading}
            isOpen={currentModal === 'unsubscribe'}
            onCancel={handleCloseModal}
            onOpenChange={() => handleCloseModal()}
            onSubmit={() => {
              submitForm();
              handleCloseModal();
            }}
          >
            {null}
          </ConfirmActionModalElement>
        )}
      </UnsubscribeAction>

      <div className="flex flex-col gap-6 w-full px-6 py-4 mx-auto min-h-dvh h-auto sm:py-8 sm:px-0 sm:w-2/3">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('errors.website_suspended.title')}</h1>
          <p className="text-gray-600">{t('errors.website_suspended.description')}</p>
        </div>

        {subscription && (
          <AccountProfileElement
            element={{
              type: 'accountProfile',
              attrs: {
                data: {
                  email: email || '',
                  firstName: firstName || '',
                  lastName: lastName || '',
                  avatarUrl: avatarUrl || '',
                  emailChangeStatusBadge: isInactive
                    ? { type: 'information', text: t('shared.cta.unsubscribed', { defaultValue: 'Unsubscribed' }) }
                    : undefined,
                },
              },
            }}
            showActions={false}
            canResetPassword={false}
            isLoading={false}
            translations={{
              'manage.account_profile.title': t('manage.account_profile.title'),
              'manage.account_profile.description': t('manage.account_profile.description'),
              'manage.account_profile.form.email_label': t('manage.account_profile.form.email_label'),
              'manage.account_profile.form.name_label': t('manage.account_profile.form.name_label'),
            }}
          >
            {null}
          </AccountProfileElement>
        )}

        {!isInactive && subscription && (
          <button
            type="button"
            onClick={() => handleOpenModal('unsubscribe')}
            className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
          >
            {t('shared.cta.unsubscribe')}
          </button>
        )}
      </div>
    </div>
  );
};

export default SuspendedPage;
